import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Paper,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  TrendingUp,
  FileText,
  Activity,
  Calendar,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { claimsmonthly, claimyears } from '../BaseURL';
import ClaimsDetails from './ClaimsDetails';

// 12 SOOTHING & EYE-FRIENDLY GRADIENTS
const gradients = [
  'linear-gradient(135deg, #a8c0ff 0%, #3f2b96 100%)',  // Calm Indigo
  'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',  // Sky Breeze
  'linear-gradient(135deg, #13547a 0%, #80d0c7 100%)',  // Ocean Mist
  'linear-gradient(135deg, #06beb6 0%, #48b1bf 100%)',  // Teal Wave
  'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',  // Deep Azure
  'linear-gradient(135deg, #8e9eab 0%, #eef2f3 100%)',  // Morning Fog
  'linear-gradient(135deg, #536976 0%, #292e49 100%)',  // Slate Gray
  'linear-gradient(135deg, #7f7fd5 0%, #86a8e7 91%, #91eae4 100%)',  // Purple Haze
  'linear-gradient(135deg, #4ca1af 0%, #c4e0e5 100%)',  // Aqua Marine
  'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',  // Navy Dusk
  'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',  // Forest Lake
  'linear-gradient(135deg, #757f9a 0%, #d7dde8 100%)',  // Silver Cloud
];

const theme = createTheme({
  palette: {
    primary: {
      main: '#2D3561',
      light: '#4A5584',
      dark: '#1A1F3A',
    },
    secondary: {
      main: '#00C9A7',
      light: '#33D4B8',
      dark: '#009B82',
    },
    background: {
      default: '#F8F9FD',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Segoe UI", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 600,
    },
  },
});

const Home = () => {
  const [claimYears, setClaimYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthlyClaims, setMonthlyClaims] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMonthData, setSelectedMonthData] = useState(null);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleViewDetails = (monthData) => {
    setSelectedMonthData(monthData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMonthData(null);
  };

  const getProgressPercentage = (value, max) => {
    return max > 0 ? (value / max) * 100 : 0;
  };

  // Load claim years on mount
  useEffect(() => {
    const loadClaimYears = async () => {
      try {
        setLoadingYears(true);
        const res = await axios.get(claimyears);
        if (res.data.success) {
          setClaimYears(res.data.years);
          setSelectedYear(res.data.years[0]);
        }
      } catch (err) {
        console.error('Failed to load claim years', err);
      } finally {
        setLoadingYears(false);
      }
    };

    loadClaimYears();
  }, []);

  // Load monthly claims when year changes
  useEffect(() => {
    if (!selectedYear) return;

    const loadMonthlyClaims = async () => {
      try {
        setLoadingClaims(true);
        const res = await axios.get(`${claimsmonthly}/${selectedYear}`);

        if (res.data.success) {
          // Add gradients to the data
          const dataWithGradients = res.data.data.map((month, index) => ({
            ...month,
            gradient: gradients[index % gradients.length]
          }));
          setMonthlyClaims(dataWithGradients);
        }
      } catch (err) {
        console.error("Failed loading monthly claims", err);
        setMonthlyClaims([]);
      } finally {
        setLoadingClaims(false);
      }
    };

    loadMonthlyClaims();
  }, [selectedYear]);

  // Calculate statistics
  const totalClaims = monthlyClaims.reduce((acc, month) => acc + month.total, 0);
  const avgMonthlyClaims = monthlyClaims.length > 0 
    ? Math.round(totalClaims / monthlyClaims.length) 
    : 0;
  const peakMonth = monthlyClaims.length > 0 
    ? Math.max(...monthlyClaims.map(m => m.total)) 
    : 0;

  // Loading skeleton for cards
  const LoadingCard = () => (
    <Grid item xs={12} sm={6} lg={4}>
      <Card sx={{ height: '100%' }}>
        <Skeleton variant="rectangular" height={120} />
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" height={30} />
          <Skeleton variant="rectangular" height={8} sx={{ my: 2 }} />
          <Skeleton variant="text" height={20} />
          <Skeleton variant="rectangular" height={8} sx={{ my: 2 }} />
          <Skeleton variant="text" height={20} />
        </CardContent>
      </Card>
    </Grid>
  );

  // Initial loading screen
  if (loadingYears) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            background: '#F8F9FD',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: '#667eea' }} />
            <Typography variant="h6" sx={{ mt: 2, color: '#2D3561' }}>
              Loading Claims Dashboard...
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: '#F8F9FD',
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          {/* Header Section */}
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              background: '#FFFFFF',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 3,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Claims Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor and track healthcare claims by month
                </Typography>
              </Box>

              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={selectedYear || ''}
                  onChange={handleYearChange}
                  disabled={loadingClaims}
                  sx={{
                    fontWeight: 600,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: '#667eea',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#764ba2',
                    },
                  }}
                  startAdornment={
                    <Calendar size={20} style={{ marginRight: 8, color: '#667eea' }} />
                  }
                >
                  {claimYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Stats Overview */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FileText size={20} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Claims
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {loadingClaims ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      totalClaims.toLocaleString()
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Activity size={20} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Avg Monthly
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {loadingClaims ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      avgMonthlyClaims.toLocaleString()
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUp size={20} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Peak Month
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {loadingClaims ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      peakMonth.toLocaleString()
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Monthly Claims Grid */}
          <Grid container spacing={3}>
            {loadingClaims ? (
              // Show 6 loading skeletons while data loads
              Array.from({ length: 6 }).map((_, index) => (
                <LoadingCard key={index} />
              ))
            ) : monthlyClaims.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No claims data available for {selectedYear}
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              monthlyClaims.map((monthData, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    {/* Gradient Header */}
                    <Box
                      sx={{
                        background: monthData.gradient,
                        height: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '30%',
                          background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.1))',
                        },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          letterSpacing: 2,
                          textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      >
                        {monthData.month}
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          color: 'white',
                          fontWeight: 800,
                          textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          mt: 1,
                        }}
                      >
                        {monthData.total.toLocaleString()}
                      </Typography>
                      <Chip
                        label="claims"
                        size="small"
                        sx={{
                          mt: 1,
                          color: 'white',
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      {/* Outpatient Progress */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            Outpatient
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="primary">
                            {monthData.outpatient.toLocaleString()}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getProgressPercentage(monthData.outpatient, monthData.total)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#E8EAF6',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {getProgressPercentage(monthData.outpatient, monthData.total).toFixed(1)}%
                          of total
                        </Typography>
                      </Box>

                      {/* Inpatient Progress */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            Inpatient
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="secondary">
                            {monthData.inpatient.toLocaleString()}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getProgressPercentage(monthData.inpatient, monthData.total)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#E0F2F1',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {getProgressPercentage(monthData.inpatient, monthData.total).toFixed(1)}%
                          of total
                        </Typography>
                      </Box>

                      {/* View Claims Button */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 3,
                          pt: 2,
                          borderTop: '1px solid #F0F0F0',
                        }}
                      >
                        <Box
                          onClick={() => handleViewDetails(monthData)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            background: monthData.gradient,
                            color: 'white',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                            },
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            View Details
                          </Typography>
                          <ChevronRight size={18} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Container>

        {/* Claims Details Dialog */}
        <ClaimsDetails
          open={dialogOpen}
          onClose={handleCloseDialog}
          monthData={selectedMonthData}
          selectedYear={selectedYear}
        />
      </Box>
    </ThemeProvider>
  );
};

export default Home;